import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../lib/translations';
import { toast } from 'sonner';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Loader2, UserPlus, CheckCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { value: 'student', labelFr: 'Étudiant', labelDe: 'Studierende/r', desc: 'Accès cours, dossier, paiements' },
  { value: 'teacher', labelFr: 'Enseignant', labelDe: 'Lehrkraft', desc: 'Gestion cours, sessions, notes' },
  { value: 'partner', labelFr: 'Partenaire', labelDe: 'Partner', desc: 'Accès candidats, messagerie' },
  { value: 'resp_admin', labelFr: 'Resp. Administratif', labelDe: 'Verwaltungsleiter', desc: 'Accès admin limité' },
  { value: 'admin', labelFr: 'Super Administrateur', labelDe: 'Super-Administrator', desc: 'Accès complet' },
];

const CreateUserPage = () => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'student',
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.password) {
      toast.error(lang === 'de' ? 'Bitte alle Pflichtfelder ausfüllen' : 'Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (form.password.length < 6) {
      toast.error(lang === 'de' ? 'Passwort muss mindestens 6 Zeichen lang sein' : 'Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/admin/create-user`, form);
      setCreated(res.data);
      toast.success(lang === 'de'
        ? `${res.data.first_name} ${res.data.last_name} wurde erstellt`
        : `${res.data.first_name} ${res.data.last_name} a été créé avec succès`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || (lang === 'de' ? 'Fehler beim Erstellen' : 'Erreur lors de la création'));
    } finally {
      setCreating(false);
    }
  };

  const handleReset = () => {
    setCreated(null);
    setForm({ first_name: '', last_name: '', email: '', password: '', role: 'student' });
  };

  const selectedRole = ROLES.find(r => r.value === form.role);

  return (
    <div className="max-w-2xl mx-auto" data-testid="create-user-page">
      <Button
        variant="ghost"
        onClick={() => navigate(-1)}
        className="mb-4"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t('action_back', lang)}
      </Button>

      {created ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">
              {lang === 'de' ? 'Benutzer erstellt!' : 'Utilisateur créé !'}
            </h2>
            <p className="text-muted-foreground mb-6">
              <strong>{created.first_name} {created.last_name}</strong> ({created.email})<br />
              {lang === 'de' ? 'Rolle' : 'Rôle'}: <span className="capitalize">{ROLES.find(r => r.value === created.role)?.[lang === 'de' ? 'labelDe' : 'labelFr']}</span>
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={handleReset} data-testid="create-another-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                {lang === 'de' ? 'Weiteren Benutzer erstellen' : 'Créer un autre utilisateur'}
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard/admin')}>
                {lang === 'de' ? 'Zurück zum Dashboard' : 'Retour au tableau de bord'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              {lang === 'de' ? 'Neuen Benutzer erstellen' : 'Créer un nouvel utilisateur'}
            </CardTitle>
            <CardDescription>
              {lang === 'de'
                ? 'Erstellen Sie ein Konto für einen Studierenden, Lehrkraft, Partner oder Administrator'
                : 'Créez un compte pour un étudiant, enseignant, partenaire ou administrateur'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Role selection */}
              <div className="space-y-2">
                <Label>{lang === 'de' ? 'Rolle *' : 'Rôle *'}</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {ROLES.map((role) => (
                    <button
                      key={role.value}
                      type="button"
                      onClick={() => update('role', role.value)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        form.role === role.value
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`role-${role.value}`}
                    >
                      <p className="font-medium text-sm">{lang === 'de' ? role.labelDe : role.labelFr}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{role.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Name */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">{t('students_first_name', lang)} *</Label>
                  <Input
                    id="first_name"
                    value={form.first_name}
                    onChange={(e) => update('first_name', e.target.value)}
                    placeholder={lang === 'de' ? 'Vorname' : 'Prénom'}
                    required
                    data-testid="first-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">{t('students_last_name', lang)} *</Label>
                  <Input
                    id="last_name"
                    value={form.last_name}
                    onChange={(e) => update('last_name', e.target.value)}
                    placeholder={lang === 'de' ? 'Nachname' : 'Nom'}
                    required
                    data-testid="last-name-input"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">{t('students_email', lang)} *</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  placeholder="email@example.com"
                  required
                  data-testid="email-input"
                />
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">{t('students_password', lang)} *</Label>
                <Input
                  id="password"
                  type="password"
                  value={form.password}
                  onChange={(e) => update('password', e.target.value)}
                  placeholder={lang === 'de' ? 'Mindestens 6 Zeichen' : 'Minimum 6 caractères'}
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>

              {/* Info box */}
              {selectedRole && (
                <div className="p-3 rounded-lg bg-muted/50 text-sm">
                  <p className="font-medium">{lang === 'de' ? selectedRole.labelDe : selectedRole.labelFr}</p>
                  <p className="text-muted-foreground text-xs mt-1">{selectedRole.desc}</p>
                  {form.role === 'student' && (
                    <p className="text-xs text-primary mt-1">
                      {lang === 'de' ? 'Ein Studierendenprofil wird automatisch erstellt' : 'Un profil étudiant sera automatiquement créé'}
                    </p>
                  )}
                  {form.role === 'partner' && (
                    <p className="text-xs text-primary mt-1">
                      {lang === 'de' ? 'Ein Partnerprofil wird automatisch erstellt' : 'Un profil partenaire sera automatiquement créé'}
                    </p>
                  )}
                </div>
              )}

              <Button
                type="submit"
                disabled={creating}
                className="w-full py-6 text-base"
                data-testid="create-user-submit"
              >
                {creating ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {lang === 'de' ? 'Erstellen...' : 'Création en cours...'}</>
                ) : (
                  <><UserPlus className="w-5 h-5 mr-2" /> {lang === 'de' ? 'Benutzer erstellen' : 'Créer l\'utilisateur'}</>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CreateUserPage;
